import React, { PropsWithChildren, useState } from 'react';
import {
  VegaAccordion,
  VegaBox,
  VegaCard,
  VegaDivider,
  VegaFlex,
  VegaFont,
} from '@globalpayments/vega-react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { Phase } from '../../configLookup/components/Props';
import styles from '../configInfo.module.css';

interface Props {
  phase: Phase;
}

interface ControlledAccordionProps extends PropsWithChildren {
  accordionId: string;
  title: string;
}

const ControlledAccordion: React.FC<ControlledAccordionProps> = ({ accordionId, title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <VegaAccordion
      accordionTitle={title}
      expand={isExpanded}
      expandIconAlign="right"
      groupId={`phase-info-${accordionId}`}
      onVegaExpand={(event) => setIsExpanded(event.detail)}
      showExpandIcon
    >
      <VegaBox padding={{ top: 'size-12' }}>{children}</VegaBox>
    </VegaAccordion>
  );
};

const Detail: React.FC<PropsWithChildren> = ({ children }) => (
  <VegaFont as="p" color="text-primary" variant="font-p2-short">
    {children}
  </VegaFont>
);

const PhaseInfo: React.FC<Props> = ({ phase }) => {
  const findVisibilityCriteria = (criteriaGroup: any): string[] => {
    let criteria: string[] = [];
    let spaceCount = 0;

    if (criteriaGroup.Clause !== undefined && criteriaGroup.Clause !== null) {
      return [`${criteriaGroup.Clause.Left.Label} ${criteriaGroup.Clause.Operator} ${criteriaGroup.Clause.Right}`];
    }

    if (criteriaGroup.Children !== undefined && criteriaGroup.Children !== null) {
      criteriaGroup.Children.forEach((child: any, index: number) => {
        spaceCount += 4;
        if (child.Clause !== null) {
          criteria = criteria.concat(findVisibilityCriteria(child));
          if (criteriaGroup.Children.length !== index + 1) {
            criteria.push(criteriaGroup.Conjunction.toUpperCase());
          }
        } else if (child.Children !== undefined && child.Children !== null) {
          child.Children.forEach((nestedChild: any, nestedIndex: number) => {
            criteria = criteria.concat(findVisibilityCriteria(nestedChild));
            if (child.Children.length !== nestedIndex + 1) {
              criteria.push(child.Conjunction.toUpperCase());
            }
          });
        }
      });
      criteria = criteria.map((criterion) => `${'\u00a0'.repeat(spaceCount)}${criterion}`);
    }

    return criteria;
  };

  const visibilityCriteria = phase.visibilityCriteria
    ? findVisibilityCriteria(phase.visibilityCriteria)
    : [];

  return (
    <VegaCard padding="size-16" variant="border">
      <VegaFlex direction="col" gap="size-12">
        <VegaFont as="h2" color="text-primary" variant="font-h4">{phase.name}</VegaFont>
        <Detail><strong>Phase Type:</strong> {phase.phaseType}</Detail>
        <VegaDivider />

        <ControlledAccordion accordionId={`phase-${phase.id}-visibility`} title="Visibility Criteria">
          <VegaFlex className={styles.visibilityCriteria} direction="col" gap="size-4">
            {visibilityCriteria.map((criterion, index) => (
              <Detail key={`${criterion}-${index}`}>{criterion}</Detail>
            ))}
          </VegaFlex>
        </ControlledAccordion>

        <ControlledAccordion accordionId={`phase-${phase.id}-events`} title="Phase Status Events">
          <VegaFlex direction="col" gap="size-8">
            {phase.phaseStatusEvents.map((event, index) => (
              <ControlledAccordion
                accordionId={`phase-${phase.id}-event-${event.leadStatusMappingId ?? 'unknown'}-${index}`}
                key={`${event.leadStatusMappingId ?? 'unknown'}-${index}`}
                title={`Event ${index}`}
              >
                <VegaFlex direction="col" gap="size-8">
                  <VegaDivider />
                  <Detail><strong>Event Type:</strong> {event.leadStatusEventType}</Detail>
                  <Detail><strong>Lead Status Text:</strong> {event.leadStatusText}</Detail>
                  <Detail><strong>Lead Type:</strong> {event.leadType}</Detail>
                  <Detail><strong>Status Type:</strong> {event.statusType}</Detail>
                </VegaFlex>
              </ControlledAccordion>
            ))}
          </VegaFlex>
        </ControlledAccordion>

        <ControlledAccordion accordionId={`phase-${phase.id}-details`} title="Phase Details">
          <VegaBox className={styles.jsonDetails}>
            <JsonView src={phase.phaseDetails} enableClipboard collapseObjectsAfterLength={5} />
          </VegaBox>
        </ControlledAccordion>
      </VegaFlex>
    </VegaCard>
  );
};

export default PhaseInfo;